import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import CsvImport from "@/components/CsvImport";
import Pagination from "@/components/Pagination";
import PageHeader from "@/components/PageHeader";

const empty = { name: "", email: "", phone: "", address: "", tax_id: "", default_payment_info: "" };
const PAGE_SIZE = 10;

export default function Customers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState([]);

  const load = async () => {
    setRefreshing(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/customers?_ts=${Date.now()}`),
        api.get("/settings/payment-options").catch(() => ({ data: { payment_options: [] } })),
      ]);
      setRows(c.data);
      setPaymentOptions(s.data?.payment_options || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editingId) await api.put(`/customers/${editingId}`, form);
      else await api.post("/customers", form);
      toast.success(editingId ? "Müşteri güncellendi" : "Müşteri eklendi");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const edit = (r) => {
    // Mevcut müşteri kayıtlarında `default_payment_info` alanı yoksa
    // da formda select kontrolü controlled kalsın.
    setForm({ ...empty, ...r });
    setEditingId(r.id);
    setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = useMemo(
    () => rows.filter((r) =>
      r.name.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")) ||
      (r.email || "").toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR"))
    ),
    [rows, q]
  );
  // Reset to page 1 whenever the search filter or dataset changes
  useEffect(() => { setPage(1); }, [q, rows.length]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <PageHeader
        kicker="Kayıt Yönetimi"
        title="Müşteriler"
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex gap-2">
            <CsvImport
              endpoint="/customers/import"
              templateUrl="/customers/import/template"
              templateName="musteri_sablonu.csv"
              title="Müşteri CSV İçe Aktar"
              testidPrefix="customers-import"
              columns={["name", "email", "phone", "tax_id", "address"]}
              onImported={load}
            />
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={() => { setForm(empty); setEditingId(null); setOpen(true); }} data-testid="new-customer-btn">
              <Plus size={16} className="mr-2" /> Yeni Müşteri
            </Button>
          </div>
        }
      />

      <Input placeholder="Ara: isim veya e-posta" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search"
        className="max-w-sm rounded-none border-2 border-[#09090B] focus-visible:ring-0" />

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Ad</TableHead>
              <TableHead className="font-mono uppercase text-xs">E-posta</TableHead>
              <TableHead className="font-mono uppercase text-xs">Telefon</TableHead>
              <TableHead className="font-mono uppercase text-xs">Vergi No</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((r) => (
              <TableRow key={r.id} data-testid={`customer-row-${r.id}`}>
                <TableCell>
                  <button
                    className="font-semibold text-left hover:text-[#0055FF] hover:underline transition-colors"
                    onClick={() => navigate(`/musteriler/${r.id}`)}
                    data-testid={`customer-name-link-${r.id}`}
                  >
                    {r.name}
                  </button>
                </TableCell>
                <TableCell className="font-mono text-sm">{r.email || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.phone || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{r.tax_id || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/musteriler/${r.id}`)} data-testid={`view-customer-${r.id}`} title="Sipariş geçmişi">
                    <Eye size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => edit(r)} data-testid={`edit-customer-${r.id}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-customer-${r.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-neutral-500">Kayıt yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} testidPrefix="customers-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{editingId ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {[
              ["name", "Ad / Firma"], ["email", "E-posta"], ["phone", "Telefon"], ["tax_id", "Vergi No"], ["address", "Adres"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs font-mono uppercase">{label}</Label>
                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid={`customer-${k}-input`} />
              </div>
            ))}
            <div className="pt-3 border-t-2 border-[#09090B]">
              <Label className="text-xs font-mono uppercase tracking-widest flex items-center justify-between">
                <span>Varsayılan Ödeme Bilgileri</span>
                <span className="text-[9px] font-normal normal-case text-neutral-500">Ayarlar &rarr; Ödeme Seçenekleri'nden gelir</span>
              </Label>
              <select
                value={form.default_payment_info || ""}
                onChange={(e) => setForm({ ...form, default_payment_info: e.target.value })}
                className="w-full rounded-none border-2 border-[#09090B] font-mono text-sm px-2 py-2 bg-white focus-visible:ring-0"
                data-testid="customer-default-payment-input"
              >
                <option value="">— Seçiniz —</option>
                {(paymentOptions || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {(!paymentOptions || paymentOptions.length === 0) && (
                <div className="mt-1 text-[10px] font-mono text-neutral-500">
                  Henüz tanımlı ödeme seçeneği yok. Ayarlar sayfasından ekleyebilirsiniz.
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={save} data-testid="save-customer-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
